import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, AlertTriangle, DollarSign } from 'lucide-react';
const GUIDE_DATA: Record<string, any> = {
  copilot: {
    name: 'GitHub Copilot',
    title: 'How much does GitHub Copilot cost per developer?',
    basePrice: '$19 to $39',
    unit: 'per user / month',
    summary: 'GitHub Copilot offers predictable per-seat pricing, but enterprise deployments often leak budget through underutilized licenses.',
    tiers: [{
      name: 'Individual',
      price: '$10/mo',
      desc: 'For personal use. Not recommended for corporate IP.'
    }, {
      name: 'Business',
      price: '$19/mo',
      desc: 'Organization-wide policy management and IP indemnity.'
    }, {
      name: 'Enterprise',
      price: '$39/mo',
      desc: 'Adds custom model fine-tuning on your codebase.'
    }],
    wasteRisk: 'High. Companies typically over-provision seats to all engineers, but internal Waydev data shows only 60-70% weekly active usage.'
  },
  cursor: {
    name: 'Cursor',
    title: 'How much does Cursor cost per developer?',
    basePrice: '$20 to $40',
    unit: 'per user / month',
    summary: 'Cursor is rapidly gaining market share with its codebase-aware features. Its business tier matches Copilot Enterprise pricing.',
    tiers: [{
      name: 'Pro',
      price: '$20/mo',
      desc: '500 fast premium requests per month.'
    }, {
      name: 'Business',
      price: '$40/mo',
      desc: 'Centralized billing, privacy mode (zero data retention), SSO.'
    }],
    wasteRisk: 'Medium-High. Similar to Copilot, you pay for the seat regardless of how many premium requests the developer actually uses.'
  },
  'claude-code': {
    name: 'Claude Code',
    title: 'How much does Claude Code cost?',
    basePrice: 'API Based',
    unit: 'pay per token',
    summary: 'Unlike IDE plugins, Claude Code operates via CLI and API consumption. Costs scale linearly with usage and context size.',
    tiers: [{
      name: 'Claude 3.5 Sonnet',
      price: '$3 / M input',
      desc: 'Standard model for coding tasks. $15 / M output tokens.'
    }, {
      name: 'Claude 3 Haiku',
      price: '$0.25 / M input',
      desc: 'Faster, cheaper model for simpler tasks. $1.25 / M output.'
    }],
    wasteRisk: 'Variable. You never pay for idle developers, but inefficient prompting with massive 200k context windows can cause sudden billing spikes.'
  },
  windsurf: {
    name: 'Windsurf',
    title: 'How much does Windsurf cost per developer?',
    basePrice: '$15 to $40',
    unit: 'per user / month',
    summary: "Codeium's Windsurf IDE offers competitive pricing with deep context awareness, targeting teams moving away from standard Copilot.",
    tiers: [{
      name: 'Pro',
      price: '$15/mo',
      desc: 'Unlimited standard completions, premium model limits.'
    }, {
      name: 'Team',
      price: '$40/mo',
      desc: 'Team analytics, centralized billing, advanced context.'
    }],
    wasteRisk: 'High. Standard per-seat SaaS model risks.'
  },
  augment: {
    name: 'Augment Code',
    title: 'How much does Augment Code cost per developer?',
    basePrice: '$50 to $100',
    unit: 'per user / month',
    summary: 'Augment Code blends a per-seat license with message-based credits, powered by a large-context "Context Engine." Spend scales with how heavily each developer prompts the agent.',
    tiers: [{
      name: 'Developer',
      price: '$50/mo',
      desc: 'Includes a monthly pool of agent messages and the full Context Engine.'
    }, {
      name: 'Pro / Max',
      price: '$100/mo',
      desc: 'Higher message limits and priority models for heavy agentic users.'
    }],
    wasteRisk: 'Medium. The seat fee creates idle-license risk, while the credit pool means heavy users can exhaust messages mid-month and trigger overage spend.'
  },
  gemini: {
    name: 'Gemini Code Assist',
    title: 'How much does Gemini Code Assist cost per developer?',
    basePrice: '$19 to $45',
    unit: 'per user / month',
    summary: "Google's Gemini Code Assist offers a notably generous free tier for individuals, with Standard and Enterprise seats backed by Gemini 2.5 and a 1M-token context window.",
    tiers: [{
      name: 'Standard',
      price: '$19/mo',
      desc: 'Team features, higher limits, and IDE integration.'
    }, {
      name: 'Enterprise',
      price: '$45/mo',
      desc: 'Annual billing, codebase awareness, and Google Cloud integration ($54 month-to-month).'
    }],
    wasteRisk: 'High. Like other per-seat tools, the generous free tier tempts teams to over-provision paid seats that go unused.'
  },
  codex: {
    name: 'OpenAI Codex',
    title: 'How much does OpenAI Codex cost?',
    basePrice: 'API Based',
    unit: 'pay per token',
    summary: 'OpenAI Codex runs via the Codex CLI and IDE agents, billed through API consumption and bundled into ChatGPT Plus/Pro/Team/Enterprise plans. Costs scale with model choice and task size.',
    tiers: [{
      name: 'GPT-5 Codex (API)',
      price: '$1.25 / M input',
      desc: 'Flagship coding model. $10 / M output tokens.'
    }, {
      name: 'Included with ChatGPT',
      price: '$20–$200/mo',
      desc: 'Codex access bundled with Plus, Pro, Team, and Enterprise seats.'
    }],
    wasteRisk: 'Variable. You never pay for idle developers on the API, but unbounded agent runs and large context windows can cause sudden billing spikes.'
  }
};
export function CostGuide() {
  const {
    tool
  } = useParams<{
    tool: string;
  }>();
  if (!tool || !GUIDE_DATA[tool]) {
    return <Navigate to="/compare" replace data-id="element-192" />;
  }
  const data = GUIDE_DATA[tool];
  return <div className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto relative" data-id="element-193">
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/5 blur-[120px] pointer-events-none rounded-full" data-id="element-194"></div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="relative z-10" data-id="element-195">
        <Link to="/compare" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 mb-10 transition-colors" data-id="element-196">
          <ArrowLeft size={16} data-id="element-197" /> Back to Comparison
        </Link>

        <div className="mb-16" data-id="element-198">
          <div className="inline-block bg-slate-900 border border-slate-800 text-slate-300 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6" data-id="element-199">
            Cost Guide: {data.name}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-50 mb-6 tracking-tight leading-tight" data-id="element-200">
            {data.title}
          </h1>
          <p className="text-xl text-slate-400 leading-relaxed" data-id="element-201">
            {data.summary}
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 mb-16 shadow-2xl flex flex-col sm:flex-row items-center gap-8 relative overflow-hidden" data-id="element-202">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-transparent pointer-events-none" data-id="element-203"></div>
          <div className="bg-emerald-500/10 p-5 rounded-2xl text-emerald-400 shrink-0 border border-emerald-500/20 glow-box-emerald" data-id="element-204">
            <DollarSign size={48} data-id="element-205" />
          </div>
          <div data-id="element-206">
            <div className="text-sm text-slate-500 font-bold uppercase tracking-widest mb-2" data-id="element-207">
              Estimated Cost
            </div>
            <div className="text-4xl md:text-5xl font-mono font-bold text-slate-50 glow-text-emerald" data-id="element-208">
              {data.basePrice}{' '}
              <span className="text-xl text-slate-500 font-sans font-normal tracking-normal" data-id="element-209">
                {data.unit}
              </span>
            </div>
          </div>
        </motion.div>

        <div className="space-y-16" data-id="element-210">
          <motion.section initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-211">
            <h2 className="text-2xl font-bold text-slate-50 mb-8" data-id="element-212">
              Pricing Tiers Breakdown
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-213">
              {data.tiers.map((tier: any, idx: number) => <div key={idx} className="border border-slate-800 rounded-xl p-8 bg-slate-900/50 hover:bg-slate-900 transition-colors" data-id="element-214">
                  <h3 className="text-xl font-bold text-slate-50 mb-3" data-id="element-215">
                    {tier.name}
                  </h3>
                  <div className="font-mono text-emerald-400 text-2xl mb-4 font-bold" data-id="element-216">
                    {tier.price}
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed" data-id="element-217">
                    {tier.desc}
                  </p>
                </div>)}
            </div>
          </motion.section>

          <motion.section initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-10" data-id="element-218">
            <h2 className="text-2xl font-bold text-slate-50 mb-6 flex items-center gap-3" data-id="element-219">
              <AlertTriangle className="text-amber-500" data-id="element-220" /> Budget Waste Risk
            </h2>
            <p className="text-slate-300 leading-relaxed mb-8 text-lg" data-id="element-221">
              {data.wasteRisk}
            </p>
            <div className="bg-slate-950 p-6 md:p-8 rounded-xl border border-slate-800" data-id="element-222">
              <h4 className="font-bold text-slate-50 mb-6 text-lg" data-id="element-223">
                How to optimize {data.name} spend:
              </h4>
              <ul className="space-y-4" data-id="element-224">
                <li className="flex items-start gap-4 text-slate-300" data-id="element-225">
                  <CheckCircle2 size={24} className="text-emerald-500 shrink-0 mt-0.5 glow-text-emerald" data-id="element-226" />
                  <span className="leading-relaxed" data-id="element-227">
                    Audit active usage monthly and de-provision licenses for
                    developers who haven't used the tool in 14 days.
                  </span>
                </li>
                <li className="flex items-start gap-4 text-slate-300" data-id="element-228">
                  <CheckCircle2 size={24} className="text-emerald-500 shrink-0 mt-0.5 glow-text-emerald" data-id="element-229" />
                  <span className="leading-relaxed" data-id="element-230">
                    Use TokenMeter to track actual API/seat utilization across
                    your engineering org.
                  </span>
                </li>
              </ul>
            </div>
          </motion.section>
        </div>
      </motion.div>
    </div>;
}